import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  KNOCK_WORKFLOW,
  PUBLIC_ID,
  ROLE,
  Roles,
} from "@highnote/server/src/core/entities";
import { DialogSection } from "App/common/Dialog";
import { ReactComponent as EyeSVG } from "App/common/icons/eye.svg";
import { ReactComponent as EyeSlashedSVG } from "App/common/icons/eye-slashed.svg";
import { ReactComponent as EnterInheritedCircleSVG } from "App/common/icons/enter-inherited-circle.svg";
import { ReactComponent as TrashSVG } from "App/common/icons/trash.svg";
import { InviteByEmail } from "./InviteByEmail";
import {
  getFullRoles,
  getInviteEmail,
  isInviteId,
  isShareId,
  getEntitySubscribers,
  hasRole,
} from "@highnote/server/src/core/shared-util";
import { UserInfo } from "App/common/UserAvatar/UserAvatar";
import { RolePicker } from "../RolePicker";
import { PermissionTooltip } from "../../PermissionTooltip";
import { getRoleSummary } from "../util";
import { useShareDialogContext } from "../ShareDialogContext";
import { MenuItem } from "App/common/Menu";
import { InheritanceIndicator } from "../InheritanceIndicator";
import { Tooltip } from "App/core/Tooltip";
import { useAuth } from "App/components/Auth";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import { useNotificationsContext } from "App/common/useNotifications";
import {
  CONTROLLED_SHARE_FEATURES,
  ControlledFeatureTooltipLabel,
} from "App/core/ControlledFeatureCallout";
import { debounce } from "lodash";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { useAttachmentsContext } from "App/common/useAttachments";
import { Divider } from "@mui/material";
import { useMobileAppParams } from "App/routes/Main/useMobileAppParams";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import {
  ROSTER_MAX_PAGES,
  ROSTER_PAGE_SIZE,
  RosterRow,
  useSpaceRoster,
} from "App/components/useEntities/useSpaceRoster";
import { reportRosterShortfall } from "App/modules/membershipTelemetry";
import { SPACE_MEMBER_TYPE } from "@highnote/server/src/core/roles";

/**
 * A local role edit that no member-document snapshot has confirmed yet: an
 * overlay, shown on top of the server value until the server catches up.
 * Keyed by `memberId`. It ships in the same commit as the roster read, not
 * as a follow-up.
 *
 * On the flagged branch this replaces the `space.updatedAt` fence: the
 * check in the component below that keeps local edits until a newer server
 * value arrives. That fence assumes every membership change bumps
 * `space.updatedAt`. Once the rendered value comes from subcollection
 * documents, the fence does not track them: the effect returns `prev` on
 * every later snapshot, and the member list freezes after the first local
 * edit. It freezes silently, with no error, and only for the person who
 * made the edit.
 *
 * `at` is wall-clock time on the client, and `member.updatedAt` is
 * wall-clock time on the server. So a client with a skewed clock can hold
 * an overlay slightly too long, or drop it slightly early. The next
 * snapshot bounds both cases, and both are strictly better than a fence
 * that never releases. Do not "fix" the skew with a comparison of role
 * arrays: an edit that sets the same roles back is a legitimate no-op
 * write, and that comparison never clears the overlay.
 */
type PendingRoleEdit = { roles: ROLE[] | null; at: number };

/**
 * One rendered roster row, normalized so the JSX below reads the same three
 * facts on both branches.
 *
 * On the legacy branch the facts come from `getFullRoles(entity)`,
 * `entity.rolesV2`, and `entity.inheritedRolesV3`. On the flagged branch
 * they come from the member document's `effectiveRoles`, `hasDirectGrant`,
 * and `inheritedRoles`, which the migration defines as exact per-key
 * mirrors of those three.
 */
type MemberRow = {
  userId: Id;
  userRoles: ROLE[] | null;
  hasDirectGrant: boolean;
  inheritedRoles: ROLE[];
};

export const ShareDialogMembers = () => {
  const { user } = useAuth();
  const { notifications, markAsRead } = useNotificationsContext();
  const {
    isEntityOwner,
    entity,
    updateEntityRoles,
    defaultFeatureAlertMessage,
    canManageEntity,
    canManageDownloadControl,
  } = useShareDialogContext();
  const { getChildTracks, getChildSpaces } = useChildEntityWatchers();
  const { attachments: spaceAttachments } = useAttachmentsContext();
  const { validMobileWebAppComponent } = useMobileAppParams();
  const fromMobileApp = Boolean(validMobileWebAppComponent);

  const subscribers = useMemo(() => getEntitySubscribers(entity), [entity]);

  const spaceTracks = getChildTracks(entity?.id);

  const spaceChildSpaces = useMemo(() => {
    return getChildSpaces(entity?.id);
  }, [entity?.id]);

  const recommendedGuests = useMemo(() => {
    const spaceAttachmentsAndTracksCreatedByGuests = [
      ...spaceTracks,
      ...spaceAttachments,
      ...spaceChildSpaces,
    ].filter((file) => !subscribers.includes(file.createdBy));

    const guestTrackCountMap = new Map<string, number>();

    // Count the number of guest tracks created by each guest
    spaceAttachmentsAndTracksCreatedByGuests.forEach((track) => {
      const { createdBy } = track;
      if (guestTrackCountMap.has(createdBy)) {
        guestTrackCountMap.set(
          createdBy,
          guestTrackCountMap.get(createdBy) + 1,
        );
      } else {
        guestTrackCountMap.set(createdBy, 1);
      }
    });

    const orderedGuestsIds = Array.from(guestTrackCountMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map((entry) => entry[0]);

    return orderedGuestsIds;
  }, [spaceTracks, subscribers]);

  const [roleSettings, setRoleSettings] = useState<{
    roles: Roles;
    updatedAt: number;
  }>({
    roles: getFullRoles(entity),
    updatedAt: entity?.updatedAt || 0,
  });

  useEffect(() => {
    if (!entity) {
      return;
    }
    setRoleSettings((prev) => {
      if (entity?.updatedAt <= prev.updatedAt) {
        return prev;
      }
      return {
        roles: getFullRoles(entity),
        updatedAt: entity.updatedAt,
      };
    });
  }, [entity]);

  useEffect(() => {
    const key = KNOCK_WORKFLOW.SPACE_MEMBER_JOINED;
    const unreads = notifications.filter(
      (n) => n.source.key === key && !n.read_at,
    );

    markAsRead(unreads);
  }, [notifications]);

  const updateRemoteEntityRoles = useMemo(() => {
    return debounce(async (newRoles: Roles, onError: () => void) => {
      try {
        await updateEntityRoles(newRoles, true);
      } catch (e) {
        onError();
      }
    }, 500);
  }, []);

  /* The roster read: under this flag, member rows come from member
   * documents instead of the maps on the space document. */
  const { membersV2Enabled } = useMembershipFlags();
  const roster = useSpaceRoster({
    spaceId: entity?.id,
    canManage: canManageEntity,
    enabled: membersV2Enabled,
  });

  /*
   * A denied or still-loading roster falls back to the in-document map. The
   * migration's dual-write contract guarantees that map is still correct
   * while the legacy fields are still written. This property is what makes
   * the flag flip safe: the worst case is today's behavior.
   */
  const useMemberDocs = membersV2Enabled && !roster.error && !roster.loading;

  const [pendingEdits, setPendingEdits] = useState<Map<Id, PendingRoleEdit>>(
    new Map(),
  );

  /*
   * Drop an overlay when the member document it applies to reports a write
   * at or after the edit. This effect runs on every roster snapshot.
   */
  useEffect(() => {
    if (pendingEdits.size === 0) return;
    setPendingEdits((prev) => {
      let changed = false;
      const next = new Map(prev);
      prev.forEach((edit, memberId) => {
        const member = roster.rowsByMemberId.get(memberId);
        /*
         * Warning: a removal and a block are different document lifecycles,
         * and the overlay must know which one it waits for. `null` deletes
         * the `rolesV2` key and the member document; `[]` keeps both, with
         * `effectiveRoles: []`. A block that waits for the document to
         * disappear hangs the overlay forever, and a removal that waits
         * for `updatedAt` hangs it forever too.
         */
        const resolved =
          edit.roles === null
            ? !member
            : !!member && (member.updatedAt || 0) >= edit.at;
        if (resolved) {
          next.delete(memberId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [roster.rowsByMemberId]);

  const updateRoleSettings = async (newRoles: Roles) => {
    const prevRoles = roleSettings;
    const prevPending = pendingEdits;
    setRoleSettings((prev) => {
      const updatedRoles = {
        ...prev.roles,
        ...newRoles,
      };
      return {
        roles: updatedRoles,
        updatedAt: Date.now(),
      };
    });
    const at = Date.now();
    setPendingEdits((prev) => {
      const next = new Map(prev);
      Object.entries(newRoles).forEach(([memberId, roles]) => {
        next.set(memberId, { roles: (roles as ROLE[]) ?? null, at });
      });
      return next;
    });
    await updateRemoteEntityRoles(newRoles, () => {
      setRoleSettings(prevRoles);
      setPendingEdits(prevPending);
    });
  };

  const userRoleOptions = useMemo(
    () => [
      ROLE.MANAGE,
      ROLE.COMMENT,
      ROLE.REPLY,
      ...(fromMobileApp ? [ROLE.REACT] : []),
      ROLE.UPLOAD,
      ROLE.DOWNLOAD,
    ],
    [fromMobileApp],
  );

  const legacyRows = useMemo<MemberRow[]>(() => {
    const inheritedRoles = entity?.inheritedRolesV3 || {};
    const entityRoles = entity?.rolesV2 || {};
    return Object.entries(roleSettings.roles).map(([userId, userRoles]) => ({
      userId,
      userRoles: userRoles as ROLE[] | null,
      hasDirectGrant: !!entityRoles[userId],
      inheritedRoles: (inheritedRoles[userId] || []) as ROLE[],
    }));
  }, [roleSettings.roles, entity]);

  const memberDocRows = useMemo<MemberRow[]>(() => {
    if (!useMemberDocs) return [];
    const byId = new Map<Id, MemberRow>();
    roster.rows.forEach((row: RosterRow) => {
      byId.set(row.memberId, {
        userId: row.memberId,
        userRoles: row.effectiveRoles,
        hasDirectGrant: row.hasDirectGrant,
        inheritedRoles: row.inheritedRoles,
      });
    });
    /*
     * Apply the overlay: `null` removes the row, `[]` blocks it, and any
     * other value replaces the row's effective roles.
     */
    pendingEdits.forEach((edit, memberId) => {
      if (edit.roles === null) {
        byId.delete(memberId);
        return;
      }
      const existing = byId.get(memberId);
      if (!existing) return;
      byId.set(memberId, { ...existing, userRoles: edit.roles });
    });
    return Array.from(byId.values());
  }, [useMemberDocs, roster.rows, pendingEdits]);

  const filteredRows = (useMemberDocs ? memberDocRows : legacyRows).filter(
    ({ userId }) => userId !== PUBLIC_ID && !isShareId(userId),
  );

  /*
   * Ordering, the most visible choice in this migration. The server query
   * is `orderBy('addedAt','desc')` because that is the only stable cursor:
   * the document id equals `memberId`, so Firestore's implicit
   * `orderBy(__name__)` completes it. That server order exists for
   * pagination only. This code re-sorts the loaded set with today's
   * comparator, copied exactly rather than derived again: admins first,
   * then `memberId.localeCompare`.
   *
   * For any space that fits in one page (50 members, the large majority)
   * the rendered order is byte-identical to today. Above 50 members,
   * `addedAt desc` chooses the set on screen, and today's comparator
   * chooses the order within it.
   */
  const adminRows = filteredRows
    .filter(({ userRoles }) => userRoles?.includes(ROLE.ADMIN))
    .sort((a, b) => a.userId.localeCompare(b.userId));

  const nonAdminRows = filteredRows
    .filter(({ userRoles }) => !userRoles?.includes(ROLE.ADMIN))
    .sort((a, b) => a.userId.localeCompare(b.userId));

  const shareMembers = [...adminRows, ...nonAdminRows];

  /*
   * The shortfall check: the only detector for member documents that are
   * silently absent.
   *
   * `memberCount` counts direct members plus the creator, and the roster
   * also renders inherited-only members, so `loaded` is normally at or
   * above `expected`. A shortfall therefore means documents are absent
   * from the query. The two ways that happens are both silent: a member
   * document with no `addedAt` (Firestore drops it from an
   * `orderBy('addedAt')` query with no error at all), and a member
   * document that was never written (the member-document write fails open:
   * a failure there does not stop the main write).
   */
  useEffect(() => {
    if (!useMemberDocs || roster.hasMore || roster.atCap) return;
    const expected = entity?.memberCount;
    if (typeof expected !== "number") return;

    const loaded = roster.rows.filter(
      (row) => row.type === SPACE_MEMBER_TYPE.USER,
    ).length;
    if (loaded < expected) {
      reportRosterShortfall(entity.id, loaded, expected);
    }
  }, [
    useMemberDocs,
    roster.rows,
    roster.hasMore,
    roster.atCap,
    entity?.id,
    entity?.memberCount,
  ]);

  const isUserManager = hasRole(user.id, ROLE.MANAGE, entity);

  return (
    <div className="ShareDialogMembers">
      {canManageEntity && (
        <DialogSection>
          <InviteByEmail
            roles={roleSettings.roles}
            onChange={updateEntityRoles}
            fromMobileApp={fromMobileApp}
          />
        </DialogSection>
      )}

      <DialogSection className="ShareDialog-members-section">
        {recommendedGuests.length > 0 && isUserManager && (
          <Fragment>
            <div>
              <h4>Recommended Guests</h4>
              {recommendedGuests.map((guestID) => (
                <div
                  className="ShareDialog-member"
                  key={`${guestID}-share-dialog`}
                >
                  <UserInfo userId={guestID}>Invited Guest</UserInfo>
                  <Button
                    theme={BUTTON_THEME.TEXT}
                    size={BUTTON_SIZE.SMALL}
                    onClick={async () =>
                      await updateEntityRoles({
                        [guestID]: [
                          ROLE.VIEW,
                          ROLE.COMMENT,
                          ROLE.REPLY,
                          ROLE.REACT,
                          ROLE.UPLOAD,
                          ROLE.DOWNLOAD,
                        ],
                      })
                    }
                  >
                    Add to Space +
                  </Button>
                </div>
              ))}
            </div>
            <Divider />
          </Fragment>
        )}
        <div>
          <h4>Members</h4>
          {shareMembers.map((row) => {
            const { userId, userRoles, hasDirectGrant, inheritedRoles } = row;
            /*
             * Warning: the "Owner" tag stays on
             * `effectiveRoles.includes(ADMIN)` and must not move to the
             * stored `isCreator`. A non-creator with a literal `[ADMIN]`
             * direct grant renders "Owner" today; a switch to `isCreator`
             * silently relabels exactly those users.
             */
            const isOwner = (userRoles || []).includes(ROLE.ADMIN);
            const hasEntityRoles = hasDirectGrant;
            const isInheriting = inheritedRoles.length > 0;
            const isBlocked = (userRoles || []).length === 0;
            const canManageParent =
              inheritedRoles.includes(ROLE.ADMIN) ||
              inheritedRoles.includes(ROLE.MANAGE);
            const inviteEmail = getInviteEmail(userId);
            const userDisabledRoles =
              (userRoles || []).includes(ROLE.DOWNLOAD) &&
              !canManageDownloadControl
                ? [
                    {
                      role: ROLE.DOWNLOAD,
                      message: isEntityOwner
                        ? ControlledFeatureTooltipLabel[
                            CONTROLLED_SHARE_FEATURES.DOWNLOAD
                          ]
                        : defaultFeatureAlertMessage,
                    },
                  ]
                : [];

            return (
              <div
                className="ShareDialog-member"
                key={`${userId}-share-dialog`}
              >
                <UserInfo userId={userId} alias={inviteEmail}>
                  {isOwner ? "" : getRoleSummary(userRoles, fromMobileApp)}
                </UserInfo>

                {isInviteId(userId) && (
                  <span className="ShareDialog-invite-tag">Invite Sent</span>
                )}

                {isOwner ? (
                  <span className="ShareDialog-owner-tag">Owner</span>
                ) : (
                  <>
                    <InheritanceIndicator
                      entity={entity}
                      userId={userId}
                      member={
                        useMemberDocs
                          ? { inheritedRoles, hasDirectGrant }
                          : undefined
                      }
                    />

                    {!isInheriting && user?.id === userId ? (
                      <Button
                        className="ShareDialog-remove-myself"
                        theme={BUTTON_THEME.TEXT}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await updateRoleSettings({
                            [userId]: null,
                          });
                        }}
                      >
                        Remove Myself
                      </Button>
                    ) : (
                      <PermissionTooltip hasPermission={canManageEntity}>
                        <div>
                          <RolePicker
                            isDisabled={!canManageEntity}
                            value={userRoles || []}
                            disabledRoles={userDisabledRoles}
                            options={isBlocked ? [] : userRoleOptions}
                            additionalOptions={[
                              <Tooltip
                                key="block-access-tooltip"
                                isDisabled={!canManageParent}
                                title="User cannot be blocked since they have manage access to the parent Collection."
                              >
                                <div>
                                  <MenuItem
                                    disabled={canManageParent}
                                    className="SpaceDialogMembers-option"
                                    key={`item-block`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateRoleSettings({
                                        [userId]: isBlocked ? [ROLE.VIEW] : [],
                                      });
                                    }}
                                  >
                                    {isBlocked ? <EyeSVG /> : <EyeSlashedSVG />}
                                    <p>
                                      <span className="title">
                                        {isBlocked
                                          ? "Unblock User"
                                          : "Block User"}
                                      </span>

                                      {isBlocked
                                        ? "Restore access in this space"
                                        : "Block User in this space"}
                                    </p>
                                  </MenuItem>
                                </div>
                              </Tooltip>,
                              ...(hasEntityRoles
                                ? [
                                    <MenuItem
                                      className="SpaceDialogMembers-option"
                                      key={`item-reset`}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        await updateRoleSettings({
                                          [userId]: null,
                                        });

                                        // If you remove the user, close the menu.
                                        const backdropEl =
                                          document.querySelector(
                                            ".RolePicker-menu .MuiBackdrop-root",
                                          ) as HTMLDivElement;
                                        if (backdropEl) backdropEl.click();
                                      }}
                                    >
                                      {isInheriting ? (
                                        <EnterInheritedCircleSVG />
                                      ) : (
                                        <TrashSVG />
                                      )}
                                      <p>
                                        <span className="title">
                                          {isInheriting
                                            ? "Undo Overrides"
                                            : "Remove User"}
                                        </span>
                                        {isInheriting
                                          ? "Restore inherited permissions"
                                          : "Remove user from this Space"}
                                      </p>
                                    </MenuItem>,
                                  ]
                                : []),
                            ]}
                            onChange={(userRoles) => {
                              updateRoleSettings({ [userId]: userRoles });
                            }}
                          />
                        </div>
                      </PermissionTooltip>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Pagination, only on the flagged branch. Without the new read
              model the roster is the whole in-document map, and there is
              nothing to page. */}
          {useMemberDocs && roster.hasMore && (
            <Button
              className="ShareDialog-load-more"
              theme={BUTTON_THEME.TEXT}
              size={BUTTON_SIZE.SMALL}
              onClick={roster.loadMore}
            >
              Show more members
            </Button>
          )}

          {/* The safe default, not the answer, for the open product question
              of very large rosters. It is not search: a search over member
              documents needs either a copy of the member's display name on
              each document (that copy does not exist, and a profile rename
              then has to update every copy) or a callable, and both
              belong to a later phase of the migration. */}
          {useMemberDocs && roster.atCap && (
            <p className="ShareDialog-members-cap">
              {`Showing the ${
                ROSTER_MAX_PAGES * ROSTER_PAGE_SIZE
              } most recently added members.`}
            </p>
          )}
        </div>
      </DialogSection>
    </div>
  );
};
